import {catchError, defaultIfEmpty, forkJoin, from, map, Observable, of, switchMap, tap} from 'rxjs';
import {AxiosError, AxiosResponse} from 'axios';
import {setEntities, UIEntitiesRef, updateAllEntities, upsertEntitiesById} from '@ngneat/elf-entities';
import {setPage, updatePaginationData} from '@ngneat/elf-pagination';

import APIAxios, {APIRoutes} from '@api/axios.api';
import SnackError from '@utils/error.utils';

import {executionsStore} from '@store/operations/executions/executions.store';
import {
  CreateBatchExecution,
  ExecutionComment,
  ExecutionDetails,
  ExecutionDetailsHaulingAndDestination,
  ExecutionDetailsService,
  ExecutionFilterDate,
  ExecutionFilters,
  ExecutionFilterSchedule,
  ExecutionFilterSource,
  ExecutionLight,
  ExecutionsGroupedAction,
  ExecutionsPagination,
  ExecutionStatusHistory,
  ExecutionTemplateMail,
  ManageExecution,
} from '@store/operations/executions/executions.model';
import {getExecutionsDataSource, getLinkedExecutionsDataSource,} from '@store/operations/executions/executions.requests';
import {Pagination, TablePagination} from '@store/common/pagination.model';
import {ServiceKind} from '@store/settings/serviceKinds';
import {IssueLight} from '@store/operations/issues';
import {executionLineService} from '@store/invoices/executionLines';
import {executionsQuery} from '@store/operations/executions/executions.query';
import {GridSortItem} from '@mui/x-data-grid-pro';
import {TableFilter} from '@store/common/tableFilter.model';
import {extractFilenameFromResponse} from '@utils/response.utils';
import {filesService} from '@store/files';
import {OrganisationType} from '@store/common/organisation.enums';

export class ExecutionsService {
  store = executionsStore;

  setSort = (sort: GridSortItem) => this.store.update((state) => ({...state, sort}));
  setPagination = (name: string) => (value: number) =>
    this.store.update((state) => ({
      ...state,
      tablePagination: {
        ...state.tablePagination,
        [name]: value,
      },
    }));

  resetFilters = () =>
    this.store.update((state) => ({
      ...state,
      filters: {search: '', tableFilters: ExecutionFilters.initTableFilters()},
    }));
  setFilters = (name: string) => (value: any) =>
    this.store.update((state) => ({
      ...state,
      filters: {
        ...state.filters,
        [name]: value,
      },
    }));

  getExecutionsWithParams = (filters: ExecutionFilters, sort: GridSortItem, tablePagination: TablePagination) => {
    const requestFilters = TableFilter.toRequestFilters(filters?.tableFilters);

    return from(
      APIAxios({
        ...APIRoutes.GETExecutions(),
        params: {
          ...requestFilters,
          searchString: filters?.search || undefined,
          onlyCreatedFromDatahub: ExecutionFilterSource.convertToParams(filters?.source),
          onlyWithSchedule: ExecutionFilterSchedule.convertToParams(filters?.schedule),
          status: filters?.status || undefined,
          from: filters?.from?.toISOString() || undefined,
          to: filters?.to?.toISOString() || undefined,
          page: tablePagination.page,
          limit: tablePagination.limit,
          sortBy: sort.field,
          orderBy: sort.sort?.toLocaleUpperCase(),
        },
      })
    ).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
      map((response: AxiosResponse<ExecutionsPagination>) => {
        return response.data;
      })
    );
  };

  countExecutionsWithParams = (filters: ExecutionFilters, sort: GridSortItem, tablePagination: TablePagination) => {
    const requestFilters = TableFilter.toRequestFilters(filters?.tableFilters);

    return from(
      APIAxios({
        ...APIRoutes.GETCountExecutions(),
        params: {
          ...requestFilters,
          searchString: filters?.search || undefined,
          onlyCreatedFromDatahub: ExecutionFilterSource.convertToParams(filters?.source),
          onlyWithSchedule: ExecutionFilterSchedule.convertToParams(filters?.schedule),
          status: filters?.status || undefined,
          from: filters?.from?.toISOString() || undefined,
          to: filters?.to?.toISOString() || undefined,
          page: tablePagination.page,
          limit: tablePagination.limit,
          sortBy: sort.field,
          orderBy: sort.sort?.toLocaleUpperCase(),
          filterDate: filters?.filterDate
        },
      })
    ).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
      map((response: AxiosResponse<ExecutionsPagination>) => {
        return response.data;
      })
    );
  };


  getExecutions = (): Observable<ExecutionsPagination> => {
    const {
      filters: {from: fromDate, to: toDate, schedule, search, source, status, tableFilters, filterDate},
      sort,
      tablePagination,
    } = this.store.getValue();
    const requestFilters = TableFilter.toRequestFilters(tableFilters);

    return from(
      APIAxios({
        ...APIRoutes.GETExecutions(),
        params: {
          ...requestFilters,
          searchString: search || undefined,
          onlyCreatedFromDatahub: ExecutionFilterSource.convertToParams(source),
          onlyWithSchedule: ExecutionFilterSchedule.convertToParams(schedule),
          status: status || undefined,
          from: fromDate?.toISOString() || undefined,
          to: toDate?.toISOString() || undefined,
          page: tablePagination.page,
          limit: tablePagination.limit,
          sortBy: sort.field,
          orderBy: sort.sort?.toLocaleUpperCase(),
          filterDate: filterDate || ExecutionFilterDate.PLANNED,
        },
      })
    ).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
      map((response: AxiosResponse<ExecutionsPagination>) => {
        return response.data;
      }),
      tap((executionPagination) => {
        const paginationData = Pagination.toPaginationData(executionPagination, tablePagination.page);

        this.store.update(
          setEntities(executionPagination.data),
          upsertEntitiesById(
            executionPagination.data.map((execution) => execution.id),
            {
              updater: (executionUi) => executionUi,
              creator: (executionId) => ({id: executionId, selected: false}),
              ref: UIEntitiesRef,
            }
          ),
          updatePaginationData(paginationData),
          setPage(
            paginationData.currentPage,
            executionPagination.data.map((execution) => execution.id)
          ),
          getExecutionsDataSource.setSuccess()
        );
      }),
      getExecutionsDataSource.trackRequestStatus()
    );
  };

  getExecutionsCSV = () => {
    const {
      filters: {from: fromDate, to: toDate, schedule, search, source, status, tableFilters},
      sort,
    } = this.store.getValue();
    const requestFilters = TableFilter.toRequestFilters(tableFilters);

    return from(
      APIAxios({
        ...APIRoutes.GETExecutionsCSV(),
        params: {
          ...requestFilters,
          disablePaginate: true,
          searchString: search || undefined,
          onlyCreatedFromDatahub: ExecutionFilterSource.convertToParams(source),
          onlyWithSchedule: ExecutionFilterSchedule.convertToParams(schedule),
          status: status || undefined,
          from: fromDate?.toISOString() || undefined,
          to: toDate?.toISOString() || undefined,
          sortBy: sort.field,
          orderBy: sort.sort?.toLocaleUpperCase(),
        },
      })
    ).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
      tap((response: AxiosResponse<Blob>) => {
        const filename = extractFilenameFromResponse(response);
        filesService.downloadDocument(response.data, filename || 'export.csv');
      }),
    );
  };

  getExecutionById = (executionId: number | string): Observable<ManageExecution[]> => {
    return from(APIAxios(APIRoutes.GETExecutionById(executionId))).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
      map((response: AxiosResponse<ExecutionDetails[]>) => {
        return response.data.map(ExecutionDetails.toManageExecution);
      }),
      map((executions: ManageExecution[]) => {
        const mainExecution = executions.find((e) => e.executionGroup?.mainExecutionId === e.id || !e.executionGroup);
        if (!mainExecution) throw new SnackError('MISSING_MAIN_EXECUTION', 'error');

        return [mainExecution].concat(executions.filter((e) => e.id !== mainExecution.id));
      })
    );
  };

  getServiceKindsByUnit = (clientUnitId: number | string): Observable<ServiceKind[]> => {
    return from(APIAxios(APIRoutes.GETServiceKindsByUnit(clientUnitId))).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
      map((response: AxiosResponse<ServiceKind[]>) => {
        return response.data;
      })
    );
  };

  getServicesByKindAndUnit = (
    clientUnitId: number | string,
    serviceKindId: number | string
  ): Observable<ExecutionDetailsService[]> => {
    return from(APIAxios(APIRoutes.GETServicesByKindAndUnit(clientUnitId, serviceKindId))).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
      map((response: AxiosResponse<ExecutionDetailsService[]>) => {
        return response.data;
      })
    );
  };

  getAvailableServicesByKindAndUnit = (clientUnitId: number | string): Observable<ExecutionDetailsService[]> => {
    return from(APIAxios(APIRoutes.GETAvailableServicesByKindAndUnit(clientUnitId))).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
      map((response: AxiosResponse<ExecutionDetailsService[]>) => {
        return response.data;
      })
    );
  };

  getHaulingAndDestinationByService = (
    clientUnitId: number | string,
    serviceId: number | string
  ): Observable<ExecutionDetailsHaulingAndDestination> => {
    return from(APIAxios(APIRoutes.GETHaulingAndDestinationByService(clientUnitId, serviceId))).pipe(
      // catchError((_) => {
      // throw new SnackError((err.response as any)?.data?.message, "error");
      // }),
      map((response: AxiosResponse<ExecutionDetailsHaulingAndDestination>) => {
        return response.data;
      })
    );
  };

  getExecutionComments = (executionId: number | string): Observable<ExecutionComment[]> => {
    return from(APIAxios(APIRoutes.GETExecutionComments(executionId))).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
      map((response: AxiosResponse<ExecutionComment[]>) => {
        return response.data;
      })
    );
  };

  addExecutionComment = (executionId: number | string, content: string): Observable<ExecutionComment> => {
    return from(
      APIAxios({
        ...APIRoutes.POSTExecutionComment(executionId),
        data: {content},
      })
    ).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
      map((response: AxiosResponse<ExecutionComment>) => {
        return response.data;
      })
    );
  };

  getExecutionIssues = (executionId: number | string): Observable<IssueLight[]> => {
    return from(APIAxios(APIRoutes.GETExecutionIssues(executionId))).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
      map((response: AxiosResponse<IssueLight[]>) => {
        return response.data;
      })
    );
  };

  getExecutionHistory = (executionId: number): Observable<ExecutionStatusHistory[]> => {
    return from(APIAxios(APIRoutes.GETExecutionHistory(executionId))).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
      map((response: AxiosResponse<ExecutionStatusHistory[]>) => {
        return response.data;
      })
    );
  };

  saveExecutions = (
    isCreation = false,
    executions: ManageExecution[]
  ): Observable<[number, number, number, ManageExecution[]]> => {
    const mainExecution = executions[0];
    const otherExecutions = executions.slice(1);

    const executionsToCreate = otherExecutions.filter((e) => e.isNew);
    const executionsToUpdate = otherExecutions.filter((e) => !e.isNew && !!e.id);

    const mainExecutionObservable = isCreation
      ? this.createExecution(
        mainExecution,
        mainExecution.parentExecutionId ? mainExecution.parentExecutionId : undefined
      )
      : this.updateExecution(mainExecution.id || '', mainExecution);

    return mainExecutionObservable.pipe(
      switchMap((mExecution) =>
        forkJoin([
          ...executionsToCreate.map((e) => this.createExecution(e, mExecution.id)),
          ...executionsToUpdate.map((e) => this.updateExecution(e.id!, {
            ...e,
            status: mExecution.status,
            plannedFrom: mExecution.plannedFrom,
            plannedTo: mExecution.plannedTo,
            executedAt: mExecution.executedAt
          })),
        ]).pipe(
          tap((e) => console.log(e)),
          defaultIfEmpty([]),
          switchMap((executions: ExecutionDetails[]) =>
            executionLineService.getExecutionLines().pipe(map(() => executions))
          ),
          switchMap((executions: ExecutionDetails[]) =>
            this.getExecutions().pipe(map(() => [mExecution, ...executions].map(ExecutionDetails.toManageExecution)))
          )
        )
      ),
      map((executions: ManageExecution[]) => [
        executionsToCreate.length + (isCreation ? 1 : 0),
        executionsToUpdate.length + (!isCreation ? 1 : 0),
        0,
        executions,
      ])
    );
  };

  createExecution = (execution: ManageExecution, parentExecutionId?: number | string): Observable<ExecutionDetails> => {
    return from(APIAxios({
      ...APIRoutes.POSTExecution(),
      data: {
        serviceId: execution.service,
        plannedFrom: execution.plannedFrom,
        plannedTo: execution.plannedTo,
        quantity1: execution.quantity1,
        parentExecutionId,
        documents: (execution.documents || []).map((d) => d.id),
        status: execution.status,
        quantity2: execution.quantity2,
        additionalInformation: execution.additionalInformation,
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<ExecutionDetails>) => {
          return response.data;
        }),
      );
  };

  createExecutionsBatch = (executions: CreateBatchExecution[]): Observable<ExecutionsPagination> => {
    return from(
      APIAxios({
        ...APIRoutes.POSTExecutionsBatch(),
        data: executions.map((e) => ({...e, serviceId: e.service?.id})),
      })
    ).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
      switchMap(() => this.getExecutions())
    );
  };

  updateExecutionDocuments = (executionId: number | string, execution: ManageExecution): Observable<any> => {
    if (!execution.documentsToAdd?.length && !execution.documentsToDelete?.length) return of(undefined);

    return from(
      APIAxios({
        ...APIRoutes.PATCHExecutionDocuments(executionId),
        data: {
          documentsToAdd: execution.documentsToAdd?.map((d) => d.id) || [],
          documentsToDelete: execution.documentsToDelete || [],
        },
      })
    ).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      })
    );
  };

  updateExecution = (executionId: number | string, execution: ManageExecution): Observable<ExecutionDetails> => {
    return this.updateExecutionDocuments(executionId, execution).pipe(
      switchMap(() => {
        return from(
          APIAxios({
            ...APIRoutes.PUTExecutionById(executionId),
            data: {
              serviceId: execution.service,
              plannedFrom: execution.plannedFrom,
              plannedTo: execution.plannedTo,
              quantity1: execution.quantity1,
              status: execution.status,
              executedAt: execution.executedAt,
              quantity2: execution.quantity2,
              parentExecutionId: execution.parentExecutionId,
              additionalInformation: execution.additionalInformation,
              distributionRules: execution.distributionRules?.map((dr) => ({
                clientOrganisationUnitId: dr.clientOrganisationUnitId,
                rateInPercent: dr.rateInPercent
              })),
            },
          })
        );
      }),
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
      map((response: AxiosResponse<ExecutionDetails>) => {
        return response.data;
      })
    );
  };

  updateExecutionInList = (
    executionId: number | string,
    execution: Partial<ManageExecution>
  ): Observable<ExecutionsPagination> => {
    return from(
      APIAxios({
        ...APIRoutes.PATCHExecutionStatusById(executionId),
        data: {status: execution.status},
      })
    ).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
      switchMap(() => this.getExecutions())
    );
  };

  setSelectedExecutions = (executionIds: number[]) => {
    this.store.update(
      updateAllEntities(
        (executionUI) => ({
          ...executionUI,
          selected: executionIds.includes(executionUI.id),
        }),
        {ref: UIEntitiesRef}
      )
    );
  };

  deleteExecution(executionId: number) {
    return from(
      APIAxios({
        ...APIRoutes.DELETEExecution(executionId),
      })
    ).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
      switchMap(() => this.getExecutions()),
      switchMap(() => executionLineService.getExecutionLines())
    );
  }

  updatesMultiplesExecution(executions: Partial<ExecutionLight>[]) {

    return from(
      APIAxios({
        ...APIRoutes.PUTExecutionMultipleUpdate(),
        data: {
          executions: executions
        },
      })
    ).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
      switchMap(() => this.getExecutions())
    );
  }

  deleteExecutions() {
    const ids = executionsQuery.selectedExecutions;

    return from(
      APIAxios({
        ...APIRoutes.DELETEExecutions(),
        data: {ids},
      })
    ).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
      switchMap(() => this.getExecutions().pipe(map(() => ids)))
    );
  }

  sendMail = (executionId: number | string, organisationType: OrganisationType, templateType: ExecutionTemplateMail) => {
    return from(APIAxios({
        ...APIRoutes.POSTMailToExecution(executionId),
        params: {
          organisationType: organisationType,
          templateType: templateType
        }
      })
    ).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      })
    );
  };

  executionsGroupedAction = (groupedAction: ExecutionsGroupedAction): Observable<number[]> => {
    const ids = executionsQuery.selectedExecutions;

    return from(
      APIAxios({
        ...APIRoutes.PATCHExecutionsGroupedAction(),
        data: {ids, ...groupedAction},
      })
    ).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
      switchMap(() => this.getExecutions().pipe(map(() => ids))),
      tap(() => this.setSelectedExecutions([]))
    );
  };

  getLinkedExecutionById = (executionId: number | string): Observable<ExecutionLight[]> => {
    return from(APIAxios(APIRoutes.GETLinkedExecutionById(executionId))).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
      map((response: AxiosResponse<ExecutionLight[]>) => {
        return response.data;
      }),
      tap((linkedExecution) => {
        this.store.update(
          (state: any) => ({
            ...state,
            linkedExecution,
          }),
          getLinkedExecutionsDataSource.setSuccess()
        );
      }),
      getLinkedExecutionsDataSource.trackRequestStatus()
    );
  };

  getAllServicesForUnit = (unitId: number, supplierOrgUnitId?: number): Observable<ExecutionDetailsService[]> => {
    return from(APIAxios({
      ...APIRoutes.GETAllServicesForClientOrganisationUnit(unitId),
      params: {
        supplierOrgUnitId,
      }
    })).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
      map((response: AxiosResponse<ExecutionDetailsService[]>) => {
        return response.data;
      })
    );
  };
}

export const executionsService = new ExecutionsService();
